import { RefObject, useEffect, useRef } from 'react';
import { CameraControls } from '@react-three/drei';
import { Group, MathUtils } from 'three';

type Vec3 = [number, number, number];

export interface Transform3D {
  position: Vec3;
  rotation: Vec3; // degrees
  scale: Vec3;
}

/**
 * Attaches a `dragging-changed` listener to a TransformControls instance.
 *
 * - Disables CameraControls while dragging to prevent camera/gizmo conflicts.
 * - Re-enables CameraControls on drag-end and fires `onDragEnd` with the
 *   final position/rotation(degrees)/scale read from `targetGroup`.
 *
 * `tcKey` should change whenever the TC unmounts and remounts (e.g. pass
 * `transformMode` or a combined string like `` `${mode}_${target}` ``).
 * `targetGroup` is also a dep so the listener re-attaches when a model mounts
 * and makes the group available for the first time.
 */
export function useTransformDrag(
  transformControlsRef: RefObject<any>,
  controlsRef: RefObject<CameraControls | null>,
  targetGroup: Group | null,
  onDragEnd: ((t: Transform3D) => void) | undefined,
  tcKey: unknown
): void {
  // Stable ref — listener always calls the latest callback without re-attaching.
  const onDragEndRef = useRef(onDragEnd);
  onDragEndRef.current = onDragEnd;

  // Stable ref — reads the latest group state at drag-end without re-attaching.
  const targetGroupRef = useRef(targetGroup);
  targetGroupRef.current = targetGroup;

  useEffect(() => {
    const tc = transformControlsRef.current;
    if (!tc) return;

    const handleDraggingChanged = (e: { value: boolean }) => {
      if (e.value) {
        if (controlsRef.current) controlsRef.current.enabled = false;
      } else {
        if (controlsRef.current) controlsRef.current.enabled = true;
        const g = targetGroupRef.current;
        if (g && onDragEndRef.current) {
          onDragEndRef.current({
            position: [g.position.x, g.position.y, g.position.z],
            rotation: [
              MathUtils.radToDeg(g.rotation.x),
              MathUtils.radToDeg(g.rotation.y),
              MathUtils.radToDeg(g.rotation.z),
            ],
            scale: [g.scale.x, g.scale.y, g.scale.z],
          });
        }
      }
    };

    tc.addEventListener('dragging-changed', handleDraggingChanged);
    return () => {
      if (controlsRef.current) controlsRef.current.enabled = true;
      tc.removeEventListener('dragging-changed', handleDraggingChanged);
    };
  // tcKey re-attaches when TC remounts; targetGroup re-attaches when model mounts.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tcKey, targetGroup]);
}
